import { compareClientWork } from '../../core/utils/clientWork';
import React, { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, LayoutGrid, ListChecks, GripVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { TaskClientBadges } from './components/TaskClientBadges';
import { PageHeader } from '../../components/shared/PageHeader';
import { useAuth } from '../../core/contexts/AuthContext';
import { useTasks } from '../../core/hooks/useTasks';
import { useClients } from '../../core/hooks/useClients';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import { useUsers } from '../../core/hooks/useUsers';
import { useTimeEntries } from '../../core/hooks/useTimeEntries';
import { useGithub } from '../../core/hooks/useGithub';
import { TaskDialog } from './components/TaskDialog';
import type { Task, TaskPriority, TaskStatus } from '../../core/models/Task';
import { TaskPriority as TaskPriorityEnum, TaskStatus as TaskStatusEnum } from '../../core/models/Task';
import { UserRole } from '../../core/models/User';
import { compareTaskVersions, formatTaskVersion, parseTaskVersion, parseVersionFromFormSelect } from '../../core/constants/taskVersionMilestone';
import { TaskFiltersBar, type TaskFilters } from './components/TaskFiltersBar';
import { buildTaskGithubPayload, buildTaskJiraPayload, toDateOrNull } from '../../core/utils/taskForm';

const EMPTY_FILTERS: TaskFilters = {
  clientId: '',
  projectId: '',
  label: '',
  version: '',
  priority: '',
  status: [],
};

const statuses: TaskStatus[] = [
  TaskStatusEnum.TODO,
  TaskStatusEnum.IN_PROGRESS,
  TaskStatusEnum.IN_REVIEW,
  TaskStatusEnum.BLOCKED,
  TaskStatusEnum.DONE,
];

function TaskCard({
  task,
  onEdit,
  projectNameById,
  clientNameById,
  actualMinutesByTaskId,
}: {
  task: Task;
  onEdit: (task: Task) => void;
  projectNameById: Record<string, string>;
  clientNameById: Record<string, string>;
  actualMinutesByTaskId: Record<string, number>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const priorityVariant = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriorityEnum.URGENT:
        return 'error';
      case TaskPriorityEnum.HIGH:
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatMinutes = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border rounded-lg p-4 mb-2 cursor-move hover:shadow-md"
      onClick={() => onEdit(task)}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-semibold mb-1">{task.title}</h4>
        <div className="flex items-center gap-2">
          <Badge variant={priorityVariant(task.priority)}>{task.priority}</Badge>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
            aria-label="Drag task"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </div>
      </div>
      {task.clientDecision && <p className="text-xs mt-2 text-indigo-700">Client: {task.clientDecision.disposition} · {task.clientDecision.priority} priority · Sentral {task.workApproval?.status || 'pending'}</p>}
      {task.description && (
        <p className="text-sm text-gray-600 line-clamp-2">{task.description}</p>
      )}
      {task.projectId && (
        <p className="text-xs text-gray-500 mt-2">
          Project: {projectNameById[task.projectId] || 'Unknown project'}
        </p>
      )}
      {task.clientId && (
        <p className="text-xs text-gray-500 mt-1">
          Client: {clientNameById[task.clientId] || task.clientId}
        </p>
      )}
      {(task.version != null || task.platform) && (
        <p className="text-xs text-gray-500 mt-2 flex flex-wrap gap-2">
          {task.platform && <Badge variant="outline">{task.platform}</Badge>}
          {task.version != null && (
            <Badge variant="outline">{formatTaskVersion(task.version)}</Badge>
          )}
        </p>
      )}
      <p className="text-xs text-gray-500 mt-2 empty:hidden">
        <TaskClientBadges task={task} />
      </p>
      {task.labels && task.labels.length > 0 && (
        <p className="text-xs text-gray-500 mt-2 flex flex-wrap gap-1">
          {task.labels.map((label) => (
            <Badge key={label} variant="secondary" className="capitalize">
              {label}
            </Badge>
          ))}
        </p>
      )}
      {task.dueDate && (
        <p className="text-xs text-gray-500 mt-2">
          Due: {new Date(task.dueDate).toLocaleDateString()}
        </p>
      )}
      <p className="text-xs text-gray-500 mt-1">
        Actual: {actualMinutesByTaskId[task.id] ? formatMinutes(actualMinutesByTaskId[task.id]) : '—'}
      </p>
    </div>
  );
}

function KanbanColumn({
  status,
  tasks,
  onEdit,
  projectNameById,
  clientNameById,
  actualMinutesByTaskId,
}: {
  status: TaskStatus;
  tasks: Task[];
  onEdit: (task: Task) => void;
  projectNameById: Record<string, string>;
  clientNameById: Record<string, string>;
  actualMinutesByTaskId: Record<string, number>;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${status}`,
    data: { type: 'column', status },
  });

  return (
    <div
      ref={setNodeRef}
      className={`border rounded-lg p-4 min-h-[500px] transition-colors ${
        isOver ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
      }`}
    >
      <h3 className="font-semibold mb-4 capitalize">{status.replace('-', ' ')}</h3>
      <SortableContext
        items={tasks.map((t) => t.id)}
        strategy={verticalListSortingStrategy}
      >
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onEdit={onEdit}
            projectNameById={projectNameById}
            clientNameById={clientNameById}
            actualMinutesByTaskId={actualMinutesByTaskId}
          />
        ))}
      </SortableContext>
    </div>
  );
}

export const TasksKanbanPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId, currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const { tasks, updateTask, createTask } = useTasks(isSuperAdmin ? null : clientId);
  const { clients, updateClient: updateProject } = useClients(isSuperAdmin ? null : clientId);
  const { organizations } = useOrganizations();
  const { users } = useUsers(isSuperAdmin ? null : clientId, true);
  const { timeEntries } = useTimeEntries({
    organizationId: isSuperAdmin ? undefined : (clientId || undefined),
  });
  const { upsertTaskIssue } = useGithub();
  const [workQueue, setWorkQueue] = useState('all');
  const [appliedFilters, setAppliedFilters] = useState<TaskFilters>(EMPTY_FILTERS);
  const clientAccounts = useMemo(() => {
    const organizationNameById = organizations.reduce((acc, organization) => {
      acc[organization.id] = organization.name;
      if (organization.legacyOrganizationId) {
        acc[organization.legacyOrganizationId] = organization.name;
      }
      return acc;
    }, {} as Record<string, string>);

    const byId = new Map<string, { id: string; name: string }>();
    for (const project of clients) {
      const id = project.clientId || '';
      if (!id) continue;
      if (!byId.has(id)) {
        byId.set(id, { id, name: organizationNameById[id] || id });
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, organizations]);
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (workQueue === 'pending' && (t.status === 'done' || t.clientDecision?.disposition !== 'requested' || (t.workApproval?.status || 'pending') !== 'pending')) return false;
      if (workQueue === 'requested' && (t.status === 'done' || t.clientDecision?.disposition !== 'requested')) return false;
      if (workQueue === 'deferred' && (t.status === 'done' || t.clientDecision?.disposition !== 'deferred')) return false;
      if (appliedFilters.clientId && t.clientId !== appliedFilters.clientId) return false;
      if (appliedFilters.projectId && t.projectId !== appliedFilters.projectId) return false;
      if (appliedFilters.priority && t.priority !== appliedFilters.priority) return false;
      if (appliedFilters.status.length > 0 && !appliedFilters.status.includes(t.status)) return false;
      if (appliedFilters.version && (t.version || '') !== appliedFilters.version) return false;
      if (appliedFilters.label) {
        const selected = appliedFilters.label.toLowerCase();
        const taskLabels = (t.labels || []).map((label) => label.trim().toLowerCase());
        if (!taskLabels.includes(selected)) return false;
      }
      return true;
    }).sort(compareClientWork);
  }, [tasks, appliedFilters, workQueue]);

  const filterLabelOptions = useMemo(() => {
    const labelMap = new Map<string, string>();
    for (const project of clients) {
      for (const rawLabel of project.taskLabels || []) {
        const label = rawLabel.trim();
        if (!label) continue;
        const key = label.toLowerCase();
        if (!labelMap.has(key)) {
          labelMap.set(key, label);
        }
      }
    }
    for (const task of tasks) {
      for (const rawLabel of task.labels || []) {
        const label = rawLabel.trim();
        if (!label) continue;
        const key = label.toLowerCase();
        if (!labelMap.has(key)) {
          labelMap.set(key, label);
        }
      }
    }
    return Array.from(labelMap.values()).sort((a, b) => a.localeCompare(b));
  }, [clients, tasks]);

  const filterVersionOptions = useMemo(() => {
    const versions = new Set<string>();
    for (const project of clients) {
      for (const rawVersion of project.taskVersions || []) {
        const parsed = parseTaskVersion(rawVersion);
        if (parsed) versions.add(parsed);
      }
    }
    for (const task of tasks) {
      if (task.version) {
        versions.add(task.version);
      }
    }
    return Array.from(versions).sort(compareTaskVersions);
  }, [clients, tasks]);

  const projectById = useMemo(() => {
    return clients.reduce((acc, project) => {
      acc[project.id] = project;
      return acc;
    }, {} as Record<string, (typeof clients)[number]>);
  }, [clients]);

  const singleProjectByClientId = useMemo(() => {
    const grouped = clients.reduce((acc, project) => {
      const cid = project.clientId || '';
      if (!cid) return acc;
      if (!acc[cid]) acc[cid] = [];
      acc[cid].push(project);
      return acc;
    }, {} as Record<string, typeof clients>);

    return Object.entries(grouped).reduce((acc, [cid, projects]) => {
      if (projects.length === 1) {
        acc[cid] = projects[0];
      }
      return acc;
    }, {} as Record<string, (typeof clients)[number]>);
  }, [clients]);

  const resolveProjectForTask = useCallback(
    (task: Task) => {
      if (task.projectId && projectById[task.projectId]) {
        return projectById[task.projectId];
      }

      if (task.clientId && singleProjectByClientId[task.clientId]) {
        return singleProjectByClientId[task.clientId];
      }

      if (task.projectId && singleProjectByClientId[task.projectId]) {
        return singleProjectByClientId[task.projectId];
      }

      return undefined;
    },
    [projectById, singleProjectByClientId]
  );

  const [localTasks, setLocalTasks] = useState<Task[]>(tasks);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  React.useEffect(() => {
    setLocalTasks(filteredTasks);
  }, [filteredTasks]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const tasksByStatus = useMemo(() => {
    return statuses.reduce((acc, status) => {
      acc[status] = localTasks.filter((task) => task.status === status);
      return acc;
    }, {} as Record<TaskStatus, Task[]>);
  }, [localTasks]);
  const projectNameById = useMemo(() => {
    return clients.reduce((acc, client) => {
      acc[client.id] = client.name;
      return acc;
    }, {} as Record<string, string>);
  }, [clients]);
  const normalizedProjectNameById = useMemo(() => {
    return localTasks.reduce((acc, task) => {
      const resolvedProject = resolveProjectForTask(task);
      if (resolvedProject) {
        acc[task.projectId || resolvedProject.id] = resolvedProject.name;
      }
      return acc;
    }, { ...projectNameById } as Record<string, string>);
  }, [localTasks, projectNameById, resolveProjectForTask]);
  const clientNameById = useMemo(() => {
    return clientAccounts.reduce((acc, client) => {
      acc[client.id] = client.name;
      return acc;
    }, {} as Record<string, string>);
  }, [clientAccounts]);

  const roundDurationToQuarterMinutes = (minutes: number) => {
    if (!Number.isFinite(minutes) || minutes <= 0) return 0;
    const rounded = Math.round(minutes / 15) * 15;
    return rounded > 0 ? rounded : 15;
  };

  const actualMinutesByTaskId = useMemo(() => {
    return timeEntries.reduce((acc, entry) => {
      if (!entry.taskId) return acc;
      const rounded = roundDurationToQuarterMinutes(Number(entry.duration) || 0);
      if (!rounded) return acc;
      acc[entry.taskId] = (acc[entry.taskId] || 0) + rounded;
      return acc;
    }, {} as Record<string, number>);
  }, [timeEntries]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeTask = localTasks.find((t) => t.id === active.id);
    if (!activeTask) return;

    const overId = over.id as string;
    const overData = over.data.current as { type?: string; status?: TaskStatus } | undefined;

    let newStatus: TaskStatus | undefined;
    if (overData?.type === 'column' && overData.status) {
      newStatus = overData.status;
    } else if (statuses.includes(overId as TaskStatus)) {
      newStatus = overId as TaskStatus;
    } else {
      newStatus = localTasks.find((t) => t.id === overId)?.status;
    }

    if (!newStatus) return;
    if (activeTask.status === newStatus) return;

    await updateTask(activeTask.id, { status: newStatus });
    if (activeTask.projectId) {
      const project = clients.find((c) => c.id === activeTask.projectId);
      const shouldWriteBack = !!(project?.githubSync?.taskWriteBack && project.githubRepoId);

      // A linked issue is opened/closed by the syncTaskIssueState function on the status write
      // above; only a task without an issue still needs one created here.
      if (shouldWriteBack && !activeTask.github?.issueNumber) {
        try {
          const issue = await upsertTaskIssue(activeTask.projectId, {
            taskId: activeTask.id,
            title: activeTask.title,
            description: activeTask.description || '',
            status: newStatus,
            priority: activeTask.priority,
            dueDate: activeTask.dueDate || null,
            version: activeTask.version ?? null,
            issueNumber: activeTask.github?.issueNumber,
          });

          await updateTask(activeTask.id, {
            github: {
              ...activeTask.github,
              repoId: issue.repoId,
              issueId: issue.issueId,
              issueNumber: issue.issueNumber,
              issueTitle: issue.issueTitle,
              issueUrl: issue.issueUrl,
            },
          });
        } catch (error) {
          console.error('GitHub issue write-back failed for status change:', error);
        }
      }
    }
    setLocalTasks(localTasks.map((task) => (
      task.id === activeTask.id ? { ...task, status: newStatus } : task
    )));
  };

  const handleCreateTask = async (data: any) => {
    const selectedClientId = data.clientId || clientId;
    if (!selectedClientId) {
      alert('Client account ID is required. Please ensure you are logged in with a valid client context.');
      return;
    }
    if (!data.projectId) {
      alert('Project is required to create a task.');
      return;
    }

    const project = clients.find((c) => c.id === data.projectId);
    const githubPayload = buildTaskGithubPayload(data, project?.githubRepoId);
    const jiraPayload = buildTaskJiraPayload(data);
    const version = parseVersionFromFormSelect(data.version);
    const estimatedHours = data.estimatedHours === '' || data.estimatedHours == null
      ? null
      : Number(data.estimatedHours);
    const taskId = await createTask({
      title: data.title,
      description: data.description || '',
      clientId: selectedClientId,
      projectId: data.projectId,
      status: (data.status || TaskStatusEnum.TODO) as TaskStatus,
      priority: (data.priority || TaskPriorityEnum.MEDIUM) as TaskPriority,
      dueDate: toDateOrNull(data.dueDate),
      estimatedHours: Number.isFinite(estimatedHours) ? estimatedHours : null,
      version,
      platform: data.platform && data.platform !== 'none' ? data.platform : null,
      labels: data.labels || [],
      assignedTo: data.assignedTo || [],
      jira: jiraPayload,
      github: githubPayload,
      createdBy: currentUser?.uid,
    });

    const shouldWriteBack = !!(project?.githubSync?.taskWriteBack && project.githubRepoId);
    if (shouldWriteBack) {
      try {
        const issue = await upsertTaskIssue(data.projectId, {
          taskId,
          title: data.title,
          description: data.description || '',
          status: data.status || TaskStatusEnum.TODO,
          priority: data.priority || TaskPriorityEnum.MEDIUM,
          dueDate: data.dueDate || null,
          version,
          issueNumber: githubPayload?.issueNumber,
        });

        await updateTask(taskId, {
          github: {
            ...githubPayload,
            repoId: issue.repoId,
            issueId: issue.issueId,
            issueNumber: issue.issueNumber,
            issueTitle: issue.issueTitle,
            issueUrl: issue.issueUrl,
          },
        });
      } catch (error) {
        console.error('GitHub issue write-back failed for created task:', error);
        alert('Task was created, but GitHub issue write-back failed.');
      }
    }
    setIsCreateDialogOpen(false);
  };

  const handleUpdateTask = async (data: any) => {
    if (!editingTask) return;
    const selectedClientId = data.clientId || editingTask.clientId;
    if (!selectedClientId || !data.projectId) {
      alert('Client and project are required.');
      return;
    }
    const project = clients.find((c) => c.id === data.projectId);
    const githubPayload = buildTaskGithubPayload(data, project?.githubRepoId);
    const jiraPayload = buildTaskJiraPayload(data);
    const version = parseVersionFromFormSelect(data.version);
    const estimatedHours = data.estimatedHours === '' || data.estimatedHours == null
      ? null
      : Number(data.estimatedHours);
    await updateTask(editingTask.id, {
      clientId: selectedClientId,
      projectId: data.projectId,
      title: data.title,
      description: data.description || '',
      status: data.status as TaskStatus,
      priority: data.priority as TaskPriority,
      dueDate: toDateOrNull(data.dueDate),
      estimatedHours: Number.isFinite(estimatedHours) ? estimatedHours : null,
      version,
      platform: data.platform && data.platform !== 'none' ? data.platform : null,
      labels: data.labels || [],
      assignedTo: data.assignedTo || [],
      jira: jiraPayload,
      github: githubPayload,
    });

    const shouldWriteBack = !!(project?.githubSync?.taskWriteBack && project.githubRepoId);
    if (shouldWriteBack) {
      try {
        const issue = await upsertTaskIssue(data.projectId, {
          taskId: editingTask.id,
          title: data.title,
          description: data.description || '',
          status: data.status,
          priority: data.priority,
          dueDate: data.dueDate || null,
          version,
          issueNumber: githubPayload?.issueNumber || editingTask.github?.issueNumber,
        });

        await updateTask(editingTask.id, {
          github: {
            ...githubPayload,
            repoId: issue.repoId,
            issueId: issue.issueId,
            issueNumber: issue.issueNumber,
            issueTitle: issue.issueTitle,
            issueUrl: issue.issueUrl,
          },
        });
      } catch (error) {
        console.error('GitHub issue write-back failed for updated task:', error);
        alert('Task was updated, but GitHub issue write-back failed.');
      }
    }
    setEditingTask(null);
  };

  const handleAddProjectVersion = async (projectId: string, version: string) => {
    const project = clients.find((c) => c.id === projectId);
    const current = project?.taskVersions || [];
    const next = Array.from(new Set([...current, version])).sort(compareTaskVersions);
    await updateProject(projectId, { taskVersions: next });
  };

  const handleAddProjectLabel = async (projectId: string, label: string) => {
    const project = clients.find((c) => c.id === projectId);
    const current = project?.taskLabels || [];
    const next = Array.from(new Set([...current, label])).sort();
    await updateProject(projectId, { taskLabels: next });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tasks Kanban Board"
        description="Drag and drop tasks to change their status"
        icon={LayoutGrid}
        actions={
          <div className="flex w-full flex-wrap gap-2 md:w-auto md:justify-end">
            <Button variant="outline" onClick={() => navigate('/tasks')}>
              <ListChecks className="mr-2 h-4 w-4" />
              List
            </Button>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Task
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">{[['all', 'All work'], ['pending', 'Client requests awaiting approval'], ['requested', 'Client requested'], ['deferred', 'Client deferred']].map(([value, label]) => <Button key={value} variant={workQueue === value ? 'default' : 'outline'} onClick={() => setWorkQueue(value)}>{label}</Button>)}</div>
      <TaskFiltersBar
        isSuperAdmin={isSuperAdmin}
        clientAccounts={clientAccounts}
        projects={clients.map((c) => ({ id: c.id, name: c.name, clientId: c.clientId || '' }))}
        labels={filterLabelOptions}
        versions={filterVersionOptions}
        filters={appliedFilters}
        onApplyFilters={setAppliedFilters}
        onClearFilters={() => setAppliedFilters(EMPTY_FILTERS)}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {statuses.map((status) => {
            return (
              <KanbanColumn
                key={status}
                status={status}
                tasks={tasksByStatus[status]}
                onEdit={(task) => {
                  const resolvedProject = resolveProjectForTask(task);
                  setEditingTask(
                    resolvedProject
                      ? { ...task, projectId: resolvedProject.id, clientId: resolvedProject.clientId || task.clientId }
                      : task
                  );
                }}
                projectNameById={normalizedProjectNameById}
                clientNameById={clientNameById}
                actualMinutesByTaskId={actualMinutesByTaskId}
              />
            );
          })}
        </div>
      </DndContext>

      <TaskDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        projects={clients.map((c) => ({
          id: c.id,
          name: c.name,
          clientId: c.clientId || '',
          jiraProjectId: c.jiraProjectId,
          taskFieldVisibility: c.taskFieldVisibility,
          taskVersions: c.taskVersions || [],
          platforms: c.platforms || [],
          taskLabels: c.taskLabels || [],
          githubRepoId: c.githubRepoId,
          githubRepoOwner: c.githubRepoOwner,
          githubRepoName: c.githubRepoName,
        }))}
        clientAccounts={clientAccounts}
        users={users}
        isSuperAdmin={isSuperAdmin}
        fixedClientId={clientId}
        onAddProjectVersion={handleAddProjectVersion}
        onAddProjectLabel={handleAddProjectLabel}
        onSubmit={handleCreateTask}
      />

      {editingTask && (
        <TaskDialog
          open={!!editingTask}
          onClose={() => setEditingTask(null)}
          task={editingTask}
          actualMinutes={actualMinutesByTaskId[editingTask.id] || 0}
          projects={clients.map((c) => ({
            id: c.id,
            name: c.name,
            clientId: c.clientId || '',
            jiraProjectId: c.jiraProjectId,
            taskFieldVisibility: c.taskFieldVisibility,
            taskVersions: c.taskVersions || [],
            platforms: c.platforms || [],
            taskLabels: c.taskLabels || [],
            githubRepoId: c.githubRepoId,
            githubRepoOwner: c.githubRepoOwner,
            githubRepoName: c.githubRepoName,
          }))}
          clientAccounts={clientAccounts}
          users={users}
          isSuperAdmin={isSuperAdmin}
          fixedClientId={clientId}
          onAddProjectVersion={handleAddProjectVersion}
          onAddProjectLabel={handleAddProjectLabel}
          onSubmit={handleUpdateTask}
        />
      )}
    </div>
  );
};
